import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState(null);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL - try both hash and query params (Safari sometimes handles differently)
        let sessionId = null;
        
        // Try hash first (standard Emergent OAuth flow)
        const hash = location.hash;
        if (hash) {
          const hashParams = new URLSearchParams(hash.replace('#', ''));
          sessionId = hashParams.get('session_id');
        }
        
        // Fallback: try query params (some browsers/redirects use query instead of hash)
        if (!sessionId) {
          const searchParams = new URLSearchParams(location.search);
          sessionId = searchParams.get('session_id');
        }

        // Debug info for troubleshooting
        const debug = {
          hash: location.hash,
          search: location.search,
          pathname: location.pathname,
          href: window.location.href,
          sessionIdFound: !!sessionId,
          sessionIdValue: sessionId ? sessionId.substring(0, 10) + '...' : null,
          userAgent: navigator.userAgent
        };
        if (process.env.NODE_ENV === 'development') {
        }
        setDebugInfo(debug);

        if (!sessionId) {
          if (process.env.NODE_ENV === 'development') {
            console.error('No session_id found in URL. Full URL:', window.location.href);
          }
          setError('No session ID received from Google. Please try again.');
          setTimeout(() => navigate('/auth'), 3000);
          return;
        }

        if (process.env.NODE_ENV === 'development') {
        }
        
        // Send session_id to backend to get user data and set cookie
        const response = await axios.post(
          `${API}/api/auth/google/callback`,
          { session_id: sessionId },
          { 
            withCredentials: true,
            timeout: 15000 // 15 second timeout for slow connections
          }
        );

        if (process.env.NODE_ENV === 'development') {
        }

        if (!response.data || !response.data.user) {
          throw new Error('Invalid response from server');
        }

        // Use auth context to persist login. Cookie was set by backend.
        if (process.env.NODE_ENV === 'development') {
        }
        login(response.data.user);

        if (process.env.NODE_ENV === 'development') {
        }
        navigate('/dashboard', { replace: true });
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Auth callback error:', error);
          console.error('Error response:', error.response?.data);
        }
        
        let errorMessage = 'Authentication failed. Please try again.';
        if (error.response?.status === 401) {
          errorMessage = 'Session expired or invalid. Please sign in again.';
        } else if (error.response?.status === 500) {
          errorMessage = 'Server error during authentication. Please try again.';
        } else if (error.code === 'ECONNABORTED') {
          errorMessage = 'Connection timed out. Please check your internet and try again.';
        } else if (error.message) {
          errorMessage = error.message;
        }
        
        setError(errorMessage);
        setTimeout(() => navigate('/auth'), 3000);
      }
    };

    // Small delay to ensure the page is fully loaded (helps with Safari)
    setTimeout(processAuth, 100);
  }, [navigate, location, login]);

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 font-medium mb-2">{error}</p>
          <p className="text-slate-500 text-sm">Redirecting to sign in...</p>
          {debugInfo && (
            <details className="mt-4 text-left text-xs text-slate-400">
              <summary className="cursor-pointer">Debug info</summary>
              <pre className="mt-2 p-2 bg-slate-100 rounded overflow-auto">
                {JSON.stringify(debugInfo, null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-12 h-12 text-[#1e3a5f] animate-spin mx-auto mb-4" />
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
